<template>
  <div
    class="slideshow-end-share"
    ref="root"
    :aria-hidden="!shown"
    :class="{ 'is-phone': sharesFiles, 'is-shown': shown }"
  >
    <!-- The film is over: share it from here, without looking for the Dela button. -->
    <div class="slideshow-end-share__row">
      <!-- Phones: the link first, at once; the film to save or post, once it's made. -->
      <!-- The film's own call: more photos for the page, then sharing. -->
      <button
        class="slideshow-end-share__primary"
        type="button"
        @click="emit('addPhotos')"
      >
        <SlideshowIcon name="images" />
        {{ texts.addPhotos }}
      </button>
      <template v-if="sharesFiles">
        <button
          class="slideshow-end-share__secondary"
          type="button"
          @click="emit('shareLink')"
        >
          <SlideshowIcon name="share" />
          {{ forFilm("linkTitle", "lifeShareTitle") }}
        </button>
        <SlideshowFilmButton
          class="slideshow-end-share__secondary"
          :percent="filmPercent"
          :state="filmState"
          @click="emit('share')"
        />
      </template>
      <template v-else>
        <!-- Computers: the services on a row of their own, the link and Spela igen under. -->
        <div class="slideshow-end-share__services">
          <button
            class="slideshow-end-share__service slideshow-end-share__copy"
            type="button"
            :aria-label="copied ? texts.copied : texts.copyLink"
            :class="{ 'is-copied': copied }"
            :title="copied ? texts.copied : texts.copyLink"
            @click="copyLink()"
          >
            <SlideshowIcon :name="copied ? 'check' : 'link'" />
          </button>
          <button
            class="slideshow-end-share__service"
            type="button"
            v-for="service in SERVICES"
            :key="service"
            :aria-label="serviceLabel(service)"
            :title="serviceLabel(service)"
            @click="shareTo(service)"
          >
            <span aria-hidden="true" v-html="SERVICE_ICONS[service]" />
          </button>
          <!-- The film itself, made on the click; a ring fills up round the icon meanwhile. -->
          <button
            class="slideshow-end-share__service slideshow-end-share__download"
            type="button"
            v-if="filmVideo"
            :aria-busy="filmMaking"
            :aria-label="filmMaking ? `${texts.preparingFilm} ${filmPercent} %` : texts.downloadFilm"
            :class="{ 'is-making': filmMaking }"
            :style="filmMaking ? { '--progress': `${filmPercent}%` } : undefined"
            :title="texts.downloadFilm"
            @click="emit('download')"
          >
            <SlideshowIcon name="downloadSolid" />
          </button>
        </div>
      </template>
      <button
        class="slideshow-end-share__again"
        type="button"
        @click="emit('replay')"
      >
        <SlideshowIcon name="play" />
        {{ texts.playAgain }}
      </button>
    </div>
    <p aria-live="polite" class="slideshow-end-share__note">
      {{ note || (copied && !sharesFiles ? texts.copied : "") }}
    </p>
  </div>
</template>

<script lang="ts" setup>
  import { onBeforeUnmount, onMounted, ref, watch } from "vue";
  import { useShareLink } from "../../composables/useShareLink";
  import { useShareServices } from "../../composables/useShareServices";
  import { forFilm, slideshowTexts } from "../../composables/useSlideshow";
  import { loadFacebookSdk } from "../../slideshow/share";
  import { track } from "../../slideshow/track";
  import SlideshowFilmButton from "./SlideshowFilmButton.vue";
  import SlideshowIcon from "./SlideshowIcon.vue";

  const props = defineProps<{
    /** The memorial page, opening on the slideshow (…#bildspel). */
    pageUrl: string;
    name: string;
    sharesFiles: boolean;
    /** The film is being made, this far (0–100). */
    filmMaking: boolean;
    filmPercent: number;
    /** The phone's Ladda ner button: plain, filling up after a tap, or ready. */
    filmState: "idle" | "making" | "ready";
    /** This browser can make the film (computers get a download button). */
    filmVideo: boolean;
    /** The film is at its end card. */
    shown: boolean;
  }>();
  // Its height, so the end card above can make room for it.
  const emit = defineEmits<{
    addPhotos: [];
    download: [];
    height: [number];
    replay: [];
    share: [];
    shareLink: [];
  }>();
  const root = ref<HTMLElement | null>(null);

  let resize: ResizeObserver | null = null;

  const texts = slideshowTexts();
  const { copied, copyLink, note, textFor, urlFor } = useShareLink(
    () => props.pageUrl,
    () => props.name,
    "end_card"
  );
  const { SERVICES, SERVICE_ICONS, serviceLabel, shareTo } = useShareServices(
    urlFor,
    textFor,
    () => props.name,
    () => copyLink(),
    "end_card"
  );

  onMounted(() => {
    if (root.value) {
      resize = new ResizeObserver(([entry]) =>
        emit("height", entry.contentRect.height)
      );
      resize.observe(root.value);
    }
  });

  watch(
    () => props.shown,
    (on) => {
      if (!on) {
        return;
      }
      track({ action: "end_share_shown" });
      if (!props.sharesFiles) {
        loadFacebookSdk(document.documentElement.lang);
      }
    },
    { immediate: true }
  );

  onBeforeUnmount(() => resize?.disconnect());
</script>
